import {
  actionsKeys,
  formatAmount,
  permissionActions,
} from "@/lib/utils";
import { setCurrentFinanceYear } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetBillingStat } from "@/services";
import {
  FileChartColumnIncreasing,
  History,
  Settings,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const useAudit = () => {
  const navigate = useNavigate();
  const [openTabs, setOpenTabs] = useState<string[]>(["invoice"]);
  const dispatch = useDispatch();

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const [auditTrailCards, setAuditTrailCards] = useState([
    {
      id: "audit-trail",
      title: "View",
      desc: "Audit Trail",
      icon: History,
      link: `${routes.auditTrail}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys?.AUDIT_TRAIL?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "reconciliation-report",
      title: "View",
      desc: "GL/CA Reconciliation Report",
      icon: FileChartColumnIncreasing,
      link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}/${routes.reconciliation}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.RECONCILIATION_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "distribution-job-configuration",
      title: "View",
      desc: "Allocation Run Time Details",
      icon: Settings,
      link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}/${routes.distributionRuntimeDetails}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys?.DISTRIBUTION_CONFIG?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: "",
        name: <span className="text-primary">{"Audit"}</span>,
      },
    ],
    []
  );

  const { data: billingStat, isLoading: isBillingStatLoading } =
    useGetBillingStat({
      communityId: communityData?.communityId,
      billingYear: currentFinanceYear,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  useEffect(() => {
    if (!billingStat) return;

    const updatedCards = auditTrailCards?.map((card: any) => {
      const { id, data: cardData, baseApiKey, apiKey } = card;
      const statData = (billingStat as any)?.data;

      if (id === "summary") {
        const updatedData = cardData.map((item: any) => ({
          ...item,
          value: item.apiKey?.toLowerCase()?.includes("amount")
            ? formatAmount(statData?.[baseApiKey]?.[item.apiKey])
            : statData?.[baseApiKey]?.[item.apiKey],
        }));

        return {
          ...card,
          data: updatedData,
          access: accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        value: statData?.[apiKey] ?? 0,
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });
    setAuditTrailCards(updatedCards);
  }, [billingStat, accessPermission])

  const onYearChange = (e: any) => {
    dispatch(setCurrentFinanceYear(e));
  };

  const onAccordionChange = (e: any) => {
    setOpenTabs(e);
  };

  const onExpandCollapse = () => {
    if (openTabs?.length) {
      setOpenTabs([]);
    } else {
      setOpenTabs(["payment", "invoice"]);
    }
  };

  const onCreateCardClick = (link: string) => () => {
    if (communityData) {
      navigate(link);
    } else {
      toast.error("Please select community");
    }
  };

  const onCardClick = (link: string) => () => {
    navigate(link);
  };

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}`);
  };

  return {
    communityData,
    currentFinanceYear,
    onYearChange,
    onAccordionChange,
    openTabs,
    onExpandCollapse,
    onCreateCardClick,
    onCardClick,
    breadcrumbData,
    onBackClick,
    auditTrailCards,
    isBillingStatLoading
  };
};

export default useAudit;
